import { Phone } from 'lucide-react';
import { Button } from '../components/ui/button';
import { useCart } from '../context/CartContext';

// WhatsApp number for Maderera Zurita (Argentina)
const WHATSAPP_NUMBER = "5491156459778";

// WhatsApp icon SVG component
const WhatsAppIcon = ({ className }) => (
    <svg 
        className={className} 
        viewBox="0 0 24 24" 
        fill="currentColor"
    >
        <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
    </svg>
);

// Get price from product - handles new API structure
const getProductPrice = (product) => {
    // Direct precio field
    if (product.precio) return product.precio;
    // Legacy precios array
    if (!product.precios || product.precios.length === 0) return null;
    const firstPrice = product.precios[0];
    if (typeof firstPrice === 'number') return firstPrice;
    if (typeof firstPrice === 'object' && firstPrice?.precio) return firstPrice.precio;
    return null;
};

const buildWhatsAppMessage = (items) => {
    let message = "¡Hola! Me interesa hacer un pedido:\n\n";
    
    items.forEach((item, index) => {
        const nombre = item.titulo || item.nombre || 'Producto';
        message += `${index + 1}. *${nombre}*`;
        if (item.categoria) {
            message += ` (${item.categoria})`;
        }
        message += `\n   Cantidad: ${item.quantity}`;
        const price = getProductPrice(item);
        if (price) {
            const formattedPrice = new Intl.NumberFormat('es-AR', {
                style: 'currency',
                currency: 'ARS',
                minimumFractionDigits: 0
            }).format(price);
            message += ` | Precio unit.: ${formattedPrice}`;
        }
        message += "\n\n";
    });
    
    message += "Por favor, confirmen disponibilidad y precio final. ¡Gracias!";
    
    return encodeURIComponent(message);
};

export const WhatsAppButton = ({ items, onOrderSent }) => {
    const { clearCart } = useCart();

    const handleClick = () => {
        if (items.length === 0) return;
        
        const message = buildWhatsAppMessage(items);
        const url = `https://wa.me/${WHATSAPP_NUMBER}?text=${message}`;
        
        // Open WhatsApp in new tab/app
        window.open(url, '_blank');
        
        // Optional: clear cart after sending
        // clearCart();
        
        if (onOrderSent) {
            onOrderSent();
        }
    };

    return (
        <Button
            onClick={handleClick}
            disabled={items.length === 0}
            className="btn-whatsapp whatsapp-pulse disabled:opacity-50 disabled:cursor-not-allowed"
            data-testid="whatsapp-order-button"
        >
            <WhatsAppIcon className="h-6 w-6" />
            <span>Hacer Pedido por WhatsApp</span>
        </Button>
    );
};

const MAX_CART_DISPLAY = 99;

// Floating WhatsApp button for mobile
export const FloatingWhatsAppButton = () => {
    const { items, totalItems } = useCart();

    if (totalItems === 0) return null;

    const handleClick = () => {
        const message = buildWhatsAppMessage(items);
        const url = `https://wa.me/${WHATSAPP_NUMBER}?text=${message}`;
        window.open(url, '_blank');
    };

    return (
        <button
            onClick={handleClick}
            className="fixed bottom-20 right-4 sm:bottom-6 sm:right-6 z-50 bg-green-500 hover:bg-green-600 text-white rounded-full p-4 shadow-lg whatsapp-pulse tap-feedback"
            data-testid="floating-whatsapp-button"
            aria-label="Hacer pedido por WhatsApp"
        >
            <WhatsAppIcon className="h-7 w-7" />
            {totalItems > 0 && (
                <span className="absolute -top-1 -right-1 bg-[#007a3f] text-white text-xs font-bold rounded-full h-5 w-5 flex items-center justify-center">
                    {totalItems > MAX_CART_DISPLAY ? `${MAX_CART_DISPLAY}+` : totalItems}
                </span>
            )}
        </button>
    );
};
